import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import LinkConstants from "../../../../constants/LinkConstants";
import BriaBadge from "../../../common/BriaBadge/BriaBadge";
import styles from "./OverviewIntegrations.module.scss";

import { useState } from "react";
import AWSIcon from "../../../../assets/Integrations/icons/aws.svg";
import AzureIcon from "../../../../assets/Integrations/icons/azure.svg";
import ComfyUIIcon from "../../../../assets/Integrations/icons/comfyui.png";
import CopilotIcon from "../../../../assets/Integrations/icons/copilot.svg";
import FalIcon from "../../../../assets/Integrations/icons/fal.svg";
import FigmaIcon from "../../../../assets/Integrations/icons/figma.png";
import HuggingFaceIcon from "../../../../assets/Integrations/icons/huggingface.svg";
import InvokeIcon from "../../../../assets/Integrations/icons/invoke.svg";
import PhotoshopIcon from "../../../../assets/Integrations/icons/photoshop.png";
import PowerPointIcon from "../../../../assets/Integrations/icons/powerpoint.svg";
import { useAppStore } from "../../../../hooks/useStores";
import FigmaInstallationPopup from "../../../../pages/NewTG/Models/Popups/FigmaInstallationPopup/FigmaInstallationPopup";
import PhotoshopInstallationPopup from "../../../../pages/NewTG/Models/Popups/PhotoshopInstallationPopup/PhotoshopInstallationPopup";

export type IntegrationOption =
	| "HUGGING_FACE"
	| "FAL"
	| "COMFY_UI"
	| "INVOKE"
	| "FIGMA"
	| "PHOTOSHOP"
	| "AWS"
	| "AZURE"
	| "COPILOT"
	| "POWERPOINT";

type IntegrationLinkCardProps = {
	icon: string;
	label: string;
	link?: LinkConstants;
	badgeLabel?: string;
	pageName?: string;
};

type OverviewIntegrationsProps = {
	cards: IntegrationOption[];
	className?: string;
	pageName?: string;
};

const iconMap: Record<IntegrationOption, string> = {
	HUGGING_FACE: HuggingFaceIcon,
	FAL: FalIcon,
	COMFY_UI: ComfyUIIcon,
	INVOKE: InvokeIcon,
	FIGMA: FigmaIcon,
	PHOTOSHOP: PhotoshopIcon,
	AWS: AWSIcon,
	AZURE: AzureIcon,
	COPILOT: CopilotIcon,
	POWERPOINT: PowerPointIcon,
};

export const OverviewIntegrations = ({ cards, className, pageName }: OverviewIntegrationsProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "overviewIntegrations" });
	const defaultIntegrations: { [key in IntegrationOption]: Omit<IntegrationLinkCardProps, "icon"> } = t("options", {
		returnObjects: true,
	});

	return (
		<Box className={`${styles.integrations} ${className}`}>
			<Typography className={styles.title}>{t("title")}</Typography>
			<Box className={styles.cards}>
				{cards.map((integration, index) => (
					<IntegrationLinkCard
						key={index}
						icon={iconMap[integration]}
						{...defaultIntegrations[integration]}
						link={LinkConstants.INTEGRATIONS[integration]}
						pageName={pageName}
					/>
				))}
			</Box>
		</Box>
	);
};

const IntegrationLinkCard = ({ icon, label, link, badgeLabel, pageName }: IntegrationLinkCardProps) => {
	const { appStore } = useAppStore();
	const isFigma = label.includes("Figma");
	const isPhotoshop = label.includes("Photoshop");
	const [popupState, setPopupState] = useState({
		figma: false,
		photoshop: false,
	});
	const closePopup = (key: keyof typeof popupState) => () => setPopupState({ ...popupState, [key]: false });

	const cardContent = (
		<>
			<Box className={styles.iconContainer}>
				<img className={styles.icon} src={icon} alt={label} />
			</Box>
			<Typography className={styles.label}>{label}</Typography>
			{badgeLabel && <BriaBadge className={styles.badge} label={badgeLabel} />}
		</>
	);

	if (pageName === "imageGeneration.overview" && (isFigma || isPhotoshop)) {
		return (
			<>
				<Box
					onClick={() => {
						if (isFigma) {
							setPopupState({ ...popupState, figma: true });
						} else if (isPhotoshop) {
							setPopupState({ ...popupState, photoshop: true });
						}
					}}
					className={styles.clickableCard}
				>
					{cardContent}
				</Box>

				<FigmaInstallationPopup
					open={popupState.figma}
					onClose={closePopup("figma")}
					onClick={appStore.downloadFigmaPlugin}
				/>
				<PhotoshopInstallationPopup
					open={popupState.photoshop}
					onClose={closePopup("photoshop")}
					onClick={appStore.downloadPhotoshopPlugin}
				/>
			</>
		);
	} else if (link) {
		return (
			<NavLink className={styles.clickableCard} to={link} target={`${!isFigma && !isPhotoshop ? "_blank" : ""}`}>
				{cardContent}
			</NavLink>
		);
	}

	return <Box className={styles.card}>{cardContent}</Box>;
};
